import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Stack, Row, Col, Form, Button } from 'react-bootstrap';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';

import FlairIcon from '../../../../../../../atomic/atoms/FlairIcon';
import { SelectSingle } from '../../../../Selects/SelectSingle';
import { useEmployeeSalariesForChangeRequestQuery } from '../../../../../__generated__/graphql';
import { formatDateShort } from '../../../../../../../utils/dateUtils';
import {
  SalaryChangeComponentProps,
  SalaryChangeData,
  SalaryChangeFormData,
  frequencyOptions,
  SalaryFrequency,
  SalaryFrequencyType,
  ChangeType,
  ChangeTypeType,
  SalaryStatus,
  DEFAULT_CURRENCY,
} from './types';
import { SalaryTiles } from './SalaryTiles';
import { SalaryComparisonBox } from './SalaryComparisonBox';
import { SalaryWarningAlert } from './SalaryWarningAlert';
import { validateDateRange } from '../utils/dateValidation';
import { DateSection } from './DateSection';

type SalaryChangeStepTwoProps = SalaryChangeComponentProps & {
  updateAmountOnly: boolean;
};

export const SalaryChangeStepTwo: React.FC<SalaryChangeStepTwoProps> = ({
  selectedEmployeeId,
  currentSalary = 0,
  currency = '$',
  onChange,
  value,
  updateAmountOnly,
  positionChangeData,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm.salaryChange');

  const isUpdatingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const {
    data: salariesData,
    loading,
    error,
  } = useEmployeeSalariesForChangeRequestQuery({
    variables: { employeeId: selectedEmployeeId || '' },
    skip: !selectedEmployeeId,
  });

  const formData = value?.formData;

  const [changeType, setChangeType] = useState<ChangeTypeType>(
    formData?.changeType || ChangeType.AMOUNT,
  );
  const [inputValue, setInputValue] = useState(formData?.value || '');
  const [effectiveDate, setEffectiveDate] = useState(
    formData?.effectiveDate || '',
  );
  const [unknownEndDate, setUnknownEndDate] = useState<boolean>(
    formData?.unknownEndDate ?? true,
  );
  const [endDate, setEndDate] = useState(formData?.endDate || '');
  const [frequency, setFrequency] = useState<SalaryFrequencyType | undefined>(
    formData?.frequency,
  );
  const [notes, setNotes] = useState(formData?.notes || '');
  const [selectedSalaryId, setSelectedSalaryId] = useState(
    formData?.selectedSalaryId || '',
  );
  const [syncDatesWithPosition, setSyncDatesWithPosition] = useState(
    formData?.syncDatesWithPosition || false,
  );

  const activeSalaries = useMemo(() => {
    if (!salariesData?.manager?.employee?.salaries) return [];
    return salariesData.manager.employee.salaries.filter(
      (salary) =>
        salary.flair__Formula_Status__c === SalaryStatus.ACTIVE ||
        salary.flair__Formula_Status__c === SalaryStatus.UPCOMING,
    );
  }, [salariesData]);

  const hasPermissionIssue = useMemo(() => {
    return (
      !loading &&
      !error &&
      salariesData?.manager?.employee &&
      salariesData.manager.employee.salaries === null
    );
  }, [loading, error, salariesData]);

  const hasNoSalary = useMemo(() => {
    return (
      !loading &&
      !error &&
      salariesData?.manager?.employee &&
      salariesData.manager.employee.salaries !== null &&
      activeSalaries.length === 0
    );
  }, [loading, error, salariesData, activeSalaries]);

  const selectedSalary = useMemo(() => {
    return activeSalaries.find((salary) => salary.Id === selectedSalaryId);
  }, [activeSalaries, selectedSalaryId]);

  const effectiveCurrentSalary = useMemo(() => {
    if (updateAmountOnly && selectedSalary) {
      return selectedSalary.flair__Amount__c;
    }
    return currentSalary;
  }, [updateAmountOnly, selectedSalary, currentSalary]);

  const effectiveCurrentFrequency = useMemo((): SalaryFrequencyType => {
    if (updateAmountOnly && selectedSalary) {
      const salaryType = String(selectedSalary.flair__Salary_Type__c);
      if (
        Object.values(SalaryFrequency).includes(
          salaryType as SalaryFrequencyType,
        )
      ) {
        return salaryType as SalaryFrequencyType;
      }
    }
    return SalaryFrequency.MONTHLY;
  }, [updateAmountOnly, selectedSalary]);

  const currentCurrency = useMemo(() => {
    if (updateAmountOnly && selectedSalary?.CurrencyIsoCode) {
      return selectedSalary.CurrencyIsoCode;
    }

    if (activeSalaries.length > 0) {
      return activeSalaries[0].CurrencyIsoCode || DEFAULT_CURRENCY;
    }

    return DEFAULT_CURRENCY;
  }, [updateAmountOnly, selectedSalary, activeSalaries]);

  const allValuesRef = useRef({
    updateAmountOnly,
    changeType,
    inputValue,
    effectiveDate,
    unknownEndDate,
    endDate,
    frequency,
    notes,
    selectedSalaryId,
    syncDatesWithPosition,
    effectiveCurrentFrequency,
    effectiveCurrentSalary,
    hasPermissionIssue,
    hasNoSalary,
    currentCurrency,
    positionChangeData,
  });

  allValuesRef.current = {
    updateAmountOnly,
    changeType,
    inputValue,
    effectiveDate,
    unknownEndDate,
    endDate,
    frequency,
    notes,
    selectedSalaryId,
    syncDatesWithPosition,
    effectiveCurrentFrequency,
    effectiveCurrentSalary,
    hasPermissionIssue,
    hasNoSalary,
    currentCurrency,
    positionChangeData,
  };

  const validationErrors = useMemo(() => {
    const effectiveStartDate =
      syncDatesWithPosition && positionChangeData?.effectiveDate
        ? positionChangeData.effectiveDate
        : effectiveDate;
    const effectiveUnknownEndDate =
      syncDatesWithPosition && positionChangeData?.unknownEndDate !== undefined
        ? positionChangeData.unknownEndDate
        : unknownEndDate;
    const effectiveEndDate =
      syncDatesWithPosition && positionChangeData?.endDate
        ? positionChangeData.endDate
        : endDate;

    return validateDateRange(
      effectiveStartDate,
      effectiveUnknownEndDate ? undefined : effectiveEndDate,
    );
  }, [
    effectiveDate,
    endDate,
    unknownEndDate,
    syncDatesWithPosition,
    positionChangeData,
  ]);

  const updateParent = useCallback(
    (updates: Partial<SalaryChangeFormData> = {}) => {
      if (isUpdatingRef.current) return;

      const values = allValuesRef.current;
      const currentFormData: SalaryChangeFormData = {
        updateAmountOnly: values.updateAmountOnly,
        changeType: values.changeType,
        value: values.inputValue,
        effectiveDate: values.effectiveDate,
        unknownEndDate: values.unknownEndDate,
        endDate: values.endDate,
        frequency: values.frequency,
        currentFrequency: values.effectiveCurrentFrequency,
        notes: values.notes,
        selectedSalaryId: values.selectedSalaryId,
        currentSalary: values.effectiveCurrentSalary,
        hasPermissionIssue: !!values.hasPermissionIssue,
        hasNoSalary: !!values.hasNoSalary,
        currency: values.currentCurrency,
        syncDatesWithPosition: values.syncDatesWithPosition,
        ...updates,
      };

      if (!currentFormData.value || parseFloat(currentFormData.value) <= 0) {
        onChangeRef.current(undefined);
        return;
      }

      const inputVal = parseFloat(currentFormData.value);
      let newValue: number;
      if (currentFormData.changeType === ChangeType.AMOUNT) {
        newValue = inputVal;
      } else {
        newValue = currentFormData.currentSalary * (1 + inputVal / 100);
      }

      const effectiveStartDate =
        currentFormData.syncDatesWithPosition &&
        values.positionChangeData?.effectiveDate
          ? values.positionChangeData.effectiveDate
          : currentFormData.effectiveDate;
      const effectiveUnknownEndDate =
        currentFormData.syncDatesWithPosition &&
        values.positionChangeData?.unknownEndDate !== undefined
          ? values.positionChangeData.unknownEndDate
          : currentFormData.unknownEndDate;
      const effectiveEndDate =
        currentFormData.syncDatesWithPosition &&
        values.positionChangeData?.endDate
          ? values.positionChangeData.endDate
          : currentFormData.endDate;

      const apiData: SalaryChangeData = {
        currentSalaryId: currentFormData.updateAmountOnly
          ? currentFormData.selectedSalaryId
          : undefined,
        isQuickUpdate: currentFormData.updateAmountOnly,
        effectiveDate: effectiveStartDate || formatDateShort(new Date()),
        newValue: Math.round(newValue * 100) / 100,
        hasPermissionIssue: currentFormData.hasPermissionIssue,
        hasNoSalary: currentFormData.hasNoSalary,
        formData: currentFormData,
      };

      if (!effectiveUnknownEndDate && effectiveEndDate) {
        apiData.endDate = effectiveEndDate;
      }

      if (!currentFormData.updateAmountOnly) {
        if (currentFormData.frequency) {
          apiData.newFrequency = currentFormData.frequency;
        }
        if (currentFormData.notes && currentFormData.notes.trim()) {
          apiData.notes = currentFormData.notes.trim();
        }
      } else {
        apiData.newFrequency = values.effectiveCurrentFrequency;
      }

      onChangeRef.current(apiData);
    },
    [],
  );

  useEffect(() => {
    if (selectedEmployeeId) {
      setChangeType(ChangeType.AMOUNT);
      setInputValue('');
      setEffectiveDate('');
      setUnknownEndDate(true);
      setEndDate('');
      setFrequency(undefined);
      setNotes('');
      setSelectedSalaryId('');
    }
  }, [selectedEmployeeId]);

  useEffect(() => {
    if (!value?.formData || isUpdatingRef.current) return;

    isUpdatingRef.current = true;
    const formData = value.formData;

    setChangeType(formData.changeType || ChangeType.AMOUNT);
    setInputValue(formData.value || '');
    setEffectiveDate(formData.effectiveDate || '');
    setUnknownEndDate(formData.unknownEndDate ?? true);
    setEndDate(formData.endDate || '');
    setFrequency(formData.frequency);
    setNotes(formData.notes || '');
    setSelectedSalaryId(formData.selectedSalaryId || '');

    isUpdatingRef.current = false;
  }, [value?.formData]);

  useEffect(() => {
    if (
      updateAmountOnly &&
      activeSalaries.length > 0 &&
      !selectedSalaryId &&
      !isUpdatingRef.current &&
      !loading
    ) {
      const activeSalary =
        activeSalaries.find(
          (s) => s.flair__Formula_Status__c === SalaryStatus.ACTIVE,
        ) || activeSalaries[0];
      setSelectedSalaryId(activeSalary.Id);
      updateParent({ selectedSalaryId: activeSalary.Id });
    }
  }, [
    updateAmountOnly,
    activeSalaries,
    selectedSalaryId,
    loading,
    updateParent,
  ]);

  useEffect(() => {
    if (!isUpdatingRef.current && !loading) {
      updateParent();
    }
  }, [
    hasPermissionIssue,
    hasNoSalary,
    effectiveCurrentSalary,
    effectiveCurrentFrequency,
    currentCurrency,
    loading,
    updateParent,
  ]);

  useEffect(() => {
    if (!updateAmountOnly && selectedSalaryId) {
      setSelectedSalaryId('');
      updateParent({ selectedSalaryId: '' });
    }
  }, [updateAmountOnly, selectedSalaryId, updateParent]);

  const handleChangeTypeToggle = useCallback(
    (type: ChangeTypeType) => {
      if (isUpdatingRef.current || !updateAmountOnly) return;
      setChangeType(type);
      updateParent({ changeType: type });
    },
    [updateAmountOnly, updateParent],
  );

  useEffect(() => {
    if (!updateAmountOnly && changeType !== ChangeType.AMOUNT) {
      setChangeType(ChangeType.AMOUNT);
      updateParent({ changeType: ChangeType.AMOUNT });
    }
  }, [updateAmountOnly, changeType, updateParent]);

  const handleFrequencyChange = useCallback(
    (value: string | null) => {
      if (isUpdatingRef.current) return;
      const newFrequency = value as SalaryFrequencyType | null;
      setFrequency(newFrequency || undefined);
      updateParent({ frequency: newFrequency || undefined });
    },
    [updateParent],
  );

  const handleSalarySelect = useCallback(
    (value: string | null) => {
      if (isUpdatingRef.current) return;
      setSelectedSalaryId(value || '');
      updateParent({ selectedSalaryId: value || '' });
    },
    [updateParent],
  );

  const handleEffectiveDateChange = useCallback(
    (value: string | null) => {
      if (isUpdatingRef.current) return;
      setEffectiveDate(value || '');
      updateParent({ effectiveDate: value || '' });
    },
    [updateParent],
  );

  const handleEndDateChange = useCallback(
    (value: string | null) => {
      if (isUpdatingRef.current) return;
      setEndDate(value || '');
      updateParent({ endDate: value || '' });
    },
    [updateParent],
  );

  const handleUnknownEndDateToggle = useCallback(
    (checked: boolean) => {
      if (isUpdatingRef.current) return;
      setUnknownEndDate(checked);
      updateParent({ unknownEndDate: checked });
    },
    [updateParent],
  );

  const handleSyncToggle = useCallback(
    (checked: boolean) => {
      setSyncDatesWithPosition(checked);
      updateParent({ syncDatesWithPosition: checked });
    },
    [updateParent],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isUpdatingRef.current) return;
      setInputValue(e.target.value);
      updateParent({ value: e.target.value });
    },
    [updateParent],
  );

  const handleNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isUpdatingRef.current) return;
      setNotes(e.target.value);
      updateParent({ notes: e.target.value });
    },
    [updateParent],
  );

  const getInputLabel = () => {
    if (changeType === ChangeType.AMOUNT) {
      return t('amount');
    }
    return t('percentage');
  };

  const getInputPlaceholder = () => {
    if (changeType === ChangeType.AMOUNT) {
      return t('enterAmount');
    }
    return t('enterPercentage');
  };

  const calculateNewValue = useCallback(() => {
    if (!inputValue || parseFloat(inputValue) <= 0) return 0;
    const inputVal = parseFloat(inputValue);
    if (changeType === ChangeType.AMOUNT) {
      return inputVal;
    }
    return effectiveCurrentSalary * (1 + inputVal / 100);
  }, [inputValue, changeType, effectiveCurrentSalary]);

  const calculateDifference = useCallback(() => {
    const newValue = calculateNewValue();
    return newValue - effectiveCurrentSalary;
  }, [calculateNewValue, effectiveCurrentSalary]);

  const hasValidInput = useMemo(() => {
    return inputValue !== '' && parseFloat(inputValue) > 0;
  }, [inputValue]);

  const calculatePercentageChange = useCallback(() => {
    if (!hasValidInput) return 0;
    const difference = calculateDifference();
    return effectiveCurrentSalary === 0
      ? 0
      : (difference / effectiveCurrentSalary) * 100;
  }, [hasValidInput, calculateDifference, effectiveCurrentSalary]);

  if (loading) {
    return (
      <div className="salary-change-section">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (hasNoSalary) {
    return (
      <div className="salary-change-section">
        <div className="text-center py-3 text-warning">
          <FlairIcon
            icon="information-circle-outline"
            size="lg"
            className="mb-2"
          />
          <div>
            <Form.Label className="text-warning mb-2">
              {t('noSalaryIssue.title')}
            </Form.Label>
          </div>
          <small className="text-muted">
            {t('noSalaryIssue.description')}
            <br />
            <strong>{t('solution')}</strong> {t('noSalaryIssue.solution')}
          </small>
        </div>
      </div>
    );
  }

  if (hasPermissionIssue) {
    return (
      <div className="salary-change-section">
        <div className="text-center py-3 text-warning">
          <FlairIcon
            icon="information-circle-outline"
            size="lg"
            className="mb-2"
          />
          <div>
            <Form.Label className="text-warning mb-2">
              {t('permissionIssue.title')}
            </Form.Label>
          </div>
          <small className="text-muted">
            {t('permissionIssue.description')}
            <br />
            <strong>{t('solution')}</strong> {t('permissionIssue.solution')}
          </small>
        </div>
      </div>
    );
  }

  return (
    <div className="salary-change-section mb-4">
      <Stack gap={3}>
        {updateAmountOnly && (
          <div>
            <div className="mb-2">
              <Form.Label>{t('selectSalary')}</Form.Label>
            </div>
            <SalaryTiles
              activeSalaries={activeSalaries}
              selectedSalaryId={selectedSalaryId}
              updateAmountOnly={updateAmountOnly}
              loading={loading}
              error={error}
              hasPermissionIssue={!!hasPermissionIssue}
              hasNoSalary={!!hasNoSalary}
              onSalarySelection={handleSalarySelect}
            />
          </div>
        )}

        {updateAmountOnly ? (
          <Stack gap={3}>
            <div>
              <Form.Label>{getInputLabel()}</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number"
                  placeholder={getInputPlaceholder()}
                  value={inputValue}
                  onChange={handleInputChange}
                  step={changeType === ChangeType.AMOUNT ? '0.01' : '0.1'}
                />
                <Button
                  variant={
                    changeType === ChangeType.AMOUNT
                      ? 'primary'
                      : 'outline-primary'
                  }
                  onClick={() => handleChangeTypeToggle(ChangeType.AMOUNT)}
                  style={{
                    minWidth: '45px',
                  }}>
                  <FlairIcon icon="dollar-circle" size="lg" className="mt-1" />
                </Button>
                <Button
                  variant={
                    changeType === ChangeType.PERCENTAGE
                      ? 'primary'
                      : 'outline-primary'
                  }
                  onClick={() => handleChangeTypeToggle(ChangeType.PERCENTAGE)}
                  style={{
                    minWidth: '45px',
                  }}>
                  <span style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                    %
                  </span>
                </Button>
              </div>
              <SalaryComparisonBox
                updateAmountOnly={updateAmountOnly}
                hasValidInput={hasValidInput}
                newValue={calculateNewValue()}
                effectiveCurrentSalary={effectiveCurrentSalary}
                effectiveCurrentFrequency={effectiveCurrentFrequency}
                difference={calculateDifference()}
                percentageChange={calculatePercentageChange()}
                frequency={effectiveCurrentFrequency}
                currency={currentCurrency}
              />
              {updateAmountOnly && (
                <SalaryWarningAlert
                  hasValidInput={hasValidInput}
                  percentageChange={calculatePercentageChange()}
                />
              )}
            </div>

            <Stack gap={3}>
              <DateSection
                effectiveDate={effectiveDate}
                endDate={endDate}
                unknownEndDate={unknownEndDate}
                syncDatesWithPosition={syncDatesWithPosition}
                positionChangeData={positionChangeData}
                validationErrors={validationErrors}
                onEffectiveDateChange={handleEffectiveDateChange}
                onEndDateChange={handleEndDateChange}
                onUnknownEndDateChange={handleUnknownEndDateToggle}
                onSyncToggle={handleSyncToggle}
                t={t}
              />
            </Stack>
          </Stack>
        ) : (
          <Stack gap={3}>
            <div>
              <Form.Label>{getInputLabel()}</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number"
                  placeholder={getInputPlaceholder()}
                  value={inputValue}
                  onChange={handleInputChange}
                  step={changeType === ChangeType.AMOUNT ? '0.01' : '0.1'}
                />
                <Button
                  variant={
                    changeType === ChangeType.AMOUNT
                      ? 'primary'
                      : 'outline-primary'
                  }
                  onClick={() => handleChangeTypeToggle(ChangeType.AMOUNT)}
                  style={{
                    minWidth: '45px',
                  }}>
                  <FlairIcon icon="dollar-circle" size="lg" className="mt-1" />
                </Button>
              </div>
              <SalaryComparisonBox
                updateAmountOnly={updateAmountOnly}
                hasValidInput={hasValidInput}
                newValue={calculateNewValue()}
                effectiveCurrentSalary={effectiveCurrentSalary}
                effectiveCurrentFrequency={effectiveCurrentFrequency}
                difference={calculateDifference()}
                percentageChange={calculatePercentageChange()}
                frequency={frequency}
                currency={currentCurrency}
              />
              {updateAmountOnly && (
                <SalaryWarningAlert
                  hasValidInput={hasValidInput}
                  percentageChange={calculatePercentageChange()}
                />
              )}
            </div>

            <Stack gap={3}>
              <DateSection
                effectiveDate={effectiveDate}
                endDate={endDate}
                unknownEndDate={unknownEndDate}
                syncDatesWithPosition={syncDatesWithPosition}
                positionChangeData={positionChangeData}
                validationErrors={validationErrors}
                onEffectiveDateChange={handleEffectiveDateChange}
                onEndDateChange={handleEndDateChange}
                onUnknownEndDateChange={handleUnknownEndDateToggle}
                onSyncToggle={handleSyncToggle}
                t={t}
              />
            </Stack>

            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t('frequency')}</Form.Label>
                  <SelectSingle
                    name="frequency"
                    value={frequency || null}
                    onChange={handleFrequencyChange}
                    options={frequencyOptions.map((option) => ({
                      value: option.value,
                      label: t('options.' + option.label),
                    }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group>
              <Form.Label>{t('notes')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notes}
                onChange={handleNotesChange}
                placeholder={t('notesPlaceholder')}
              />
            </Form.Group>
          </Stack>
        )}
      </Stack>
    </div>
  );
};
